import { concatTypedArrays, reverseBitsInBytes, buf2hex } from "../util";

const COMMAND_SPI = 0xA8;
const COMMAND_GPIO_START = 0xAB;
const COMMAND_GPIO_SET = 0x80;
const COMMAND_GPIO_END = 0x20;

class CH341a {
    constructor() {
        this.debug = false;
        this.name = "CH341 based programmer"
        this.description = "A WCH CH341a based programmer. Many cheap programmers are based on this chip. Note that some of these require a \"Voltage Fix\" to make sure that the signal lines are using the correct voltage."
        this.read_size = 4096
        this.usb_filter = [{ vendorId: 0x1A86, productId: 0x5512 }]
    }

    static async requestPort() {
        return navigator.usb.requestDevice({
            filters: [{ vendorId: 0x1A86, productId: 0x5512 }]
        });
    }

    async getDevice() {
        this.ready = false;
        console.log("Requesting device...");
        const device = await CH341a.requestPort();
        if(device)
            this.setupDevice(device)
    }

    async setupDevice(device) {
        this.device = device
        try {
            console.log("Opening device...");
            await this.device.open();

            console.log("Selecting configuration...");
            await this.device.selectConfiguration(1);

            console.log("Claiming interface...");
            await this.device.claimInterface(0);

            console.log("Sending first transfer...");
            await this.device.transferOut(0x02, new Uint8Array([0xaa, 0x61, 0x00]));

            console.log("Sending second transfer...");
            await this.device.transferOut(0x02, new Uint8Array([0xab, 0xb7, 0x7f, 0x20]));
            await this.device.transferOut(0x02, new Uint8Array([0xab, 0xb7, 0xb7, 0xb7, 0xb6, 0x20]));

            this.ready = true;
            console.log("Device setup complete!");
        } catch (error) {
            console.error("Error setting up the device:", error);
            throw error; // Rethrow to handle the error in the caller if needed
        }
    }

    disconnect() {
        this.ready = false;
        this.device.close()
    }

    async selectChip() {
        const data = new Uint8Array([COMMAND_GPIO_START,
            COMMAND_GPIO_SET|0x37,
            COMMAND_GPIO_SET|0x37,
            COMMAND_GPIO_SET|0x37,
            // re-enable CS
            COMMAND_GPIO_SET | 0x36,
            COMMAND_GPIO_END
         ]);
         await this.device.transferOut(0x2, data)
    }
    async spiTransfer(write, read_length) {
        await this.selectChip();
        const read_buffer = new Uint8Array(read_length);
        read_buffer.fill(0xff, 0x0, read_length);
        const payload = concatTypedArrays(write, read_buffer);
        const payload_reversed = reverseBitsInBytes(payload);
    
        const prefix = 0xa8;
    
        // Get the interface, alternate setting, and endpoints
        const interfaceNumber = 0; // Adjust if your device uses a different interface
        const alternateSetting = 0; // Adjust if necessary
        const usbInterface = this.device.configuration.interfaces[interfaceNumber];
        const usbAlternate = usbInterface.alternates[alternateSetting];
    
        // Get OUT endpoint (direction === 'out') and IN endpoint (direction === 'in')
        const endpointOut = usbAlternate.endpoints.find(e => e.direction === "out");
        const endpointIn = usbAlternate.endpoints.find(e => e.direction === "in");
    
        if (!endpointOut || !endpointIn) {
            throw new Error("Could not find IN or OUT endpoints");
        }
    
        const endpointNumberOut = endpointOut.endpointNumber;
        const endpointNumberIn = endpointIn.endpointNumber;
    
        const maxPacketSizeOut = endpointOut.packetSize;
        const maxPacketSizeIn = endpointIn.packetSize;
    
        // Adjust packetSize to maximize throughput
        const packetSize = maxPacketSizeOut - 1; // Reserve 1 byte for the prefix
    
        const totalPackets = Math.ceil(payload_reversed.length / packetSize);
        const result_buffer = new Uint8Array(totalPackets * maxPacketSizeIn);
    
        // Collect promises for concurrent execution
        const transferOutPromises = [];
        const transferInPromises = [];
    
        let read_offset = 0;
    
        for (let i = 0; i < payload_reversed.length; i += packetSize) {
            // Slice out a chunk of data
            const chunk = payload_reversed.slice(i, i + packetSize);
    
            // Create a packet with the prefix
            const packet = new Uint8Array(chunk.length + 1);
            packet[0] = prefix; // Add the 0xa8 prefix
            packet.set(chunk, 1); // Set the chunk starting at index 1
    
            if (this.debug) {
                console.log("Writing " + buf2hex(packet));
            }
    
            // Start the transferOut without awaiting
            transferOutPromises.push(this.device.transferOut(endpointNumberOut, packet));
    
            // Prepare the transferIn promise
            transferInPromises.push(this.device.transferIn(endpointNumberIn, maxPacketSizeIn));
        }
    
        // Wait for all transferOut operations to complete
        await Promise.all(transferOutPromises);
    
        // Wait for all transferIn operations to complete
        const readResults = await Promise.all(transferInPromises);
    
        // Process readResults
        readResults.forEach((readResult) => {
            const r = new Uint8Array(readResult.data.buffer);
            result_buffer.set(r, read_offset);
            read_offset += r.length;
        });
    
        // Reverse bits in the result buffer
        const result = reverseBitsInBytes(result_buffer.slice(0, read_offset));
        if (this.debug) {
            console.log("Full read: " + buf2hex(result));
        }
        return result;
    }
}    

export {CH341a}
